import type { GetThreadResp } from "@/types/thread.ts";

// The thread list as both apps hold it in their React Query caches: one
// `ThreadListPage` per fetched page, each served by `groupBy=pinned`. The
// helpers here are the only code that knows the page shape, so the two clients
// flatten, patch and regroup it the same way.

/** The fields the grouping reads. A cached row is a full `GetThreadResp`; tests build narrower rows. */
export type ThreadListRow = Pick<GetThreadResp, "id" | "pinnedAt" | "sortDate">;

/**
 * One page of the list. The server fills `pinned` on page 0 only and offset-pages
 * `rest`, so `rest.length` is what decides whether another page exists.
 */
export type ThreadListPage<T extends ThreadListRow = GetThreadResp> = {
  pinned: T[];
  rest: T[];
};

/** The two groups the list renders, flattened across pages. */
export type ThreadListGroups<T extends ThreadListRow = GetThreadResp> = {
  pinned: T[];
  rest: T[];
};

/**
 * Read a list response as a page, whichever shape the server sent.
 *
 * Web and the API deploy from the same merge but finish at different times, so
 * for a few minutes a bundle that asks for `groupBy=pinned` can be answered by
 * an API that does not know the param and returns the flat page with the pins
 * hoisted to the front. That page splits at its leading run of `pinnedAt`,
 * which is exactly the two groups the server would have served.
 */
export const asThreadListPage = <T extends ThreadListRow>(
  response: ThreadListPage<T> | T[],
): ThreadListPage<T> => {
  if (!Array.isArray(response)) return response;
  const firstUnpinned = response.findIndex((row) => !row.pinnedAt);
  const cut = firstUnpinned === -1 ? response.length : firstUnpinned;
  return { pinned: response.slice(0, cut), rest: response.slice(cut) };
};

/** Apply `map` to every row of every page, keeping each row in its group and slot. */
export const mapThreadListPages = <T extends ThreadListRow>(
  pages: readonly ThreadListPage<T>[],
  map: (row: T) => T,
): ThreadListPage<T>[] =>
  pages.map((page) => ({ pinned: page.pinned.map(map), rest: page.rest.map(map) }));

/** Every row of every page, pins first, in page order. */
export const threadListRows = <T extends ThreadListRow>(pages: readonly ThreadListPage<T>[]): T[] =>
  pages.flatMap((page) => [...page.pinned, ...page.rest]);

/**
 * Flatten the loaded pages into the two groups the list renders, each thread
 * at most once.
 *
 * Pages are fetched one request at a time, and the pinned group is served on
 * page 0 while the remainder is cut by `pinnedAt IS NULL` on every page. A
 * thread unpinned after page 0 loaded therefore still sits in the cached
 * pinned group when a later page returns it in the remainder, and a `sortDate`
 * bump between fetches can shift a row across an offset boundary onto two
 * pages. Both clients key rows by thread id, so a duplicate breaks row identity
 * until the refetch lands. Keeping the first occurrence — pins before the
 * remainder, earlier pages before later ones — is what the list would show once
 * that refetch reconciles it.
 */
export const flattenThreadListPages = <T extends ThreadListRow>(
  pages: readonly ThreadListPage<T>[],
): ThreadListGroups<T> => {
  const seen = new Set<number>();
  const firstOccurrences = (rows: T[]) =>
    rows.filter((row) => {
      if (seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    });
  const pinned = firstOccurrences(pages.flatMap((page) => page.pinned));
  const rest = firstOccurrences(pages.flatMap((page) => page.rest));
  return { pinned, rest };
};

/** Milliseconds for a date the cache may hold as a `Date` or, off the wire, as an ISO string. */
const time = (value: Date | string | null | undefined) =>
  value ? new Date(value).getTime() : Number.NaN;

/**
 * Move a thread to the group its `pinnedAt` now says it belongs in, so a pin
 * or unpin regroups the row the moment its diff lands rather than when the
 * list refetches.
 *
 * The row is read from the pages themselves, after the diff has been merged
 * into it, so this needs no knowledge of what changed. A pinned row goes to
 * page 0's pinned group, newest pin first. An unpinned row goes back into the
 * remainder at the slot its `sortDate` earns under `order`, in whichever loaded
 * page holds that slot — an approximation of the server's offset windows that
 * the refetch following every pin change corrects. A thread the pages do not
 * hold is left to that refetch, as is a list with no page 0 to pin into.
 *
 * `awaitsUser` is passed when the list is ordered needs-you first. It seats a
 * row by that bucket ahead of its date, in the pinned group and the remainder
 * alike, because the server buckets both. Omit it and the date alone decides.
 *
 * Omitting it on a needs-you-first list is visible rather than subtle: the
 * optimistic insert seats the row the user just acted on by date, and the
 * refetch that every pin change triggers then moves it, so the row jumps under
 * the cursor that acted on it.
 *
 * Returns the same array when nothing moved, so a cache update can be skipped.
 */
export const regroupThread = <T extends ThreadListRow>(
  pages: ThreadListPage<T>[],
  threadId: number,
  order: "asc" | "desc" = "desc",
  awaitsUser?: (row: T) => boolean,
): ThreadListPage<T>[] => {
  const row = threadListRows(pages).find((candidate) => candidate.id === threadId);
  if (!row || pages.length === 0) return pages;

  const isPinned = Boolean(row.pinnedAt);
  const inRightGroup = pages.some((page) =>
    (isPinned ? page.pinned : page.rest).some((candidate) => candidate.id === threadId),
  );
  const inWrongGroup = pages.some((page) =>
    (isPinned ? page.rest : page.pinned).some((candidate) => candidate.id === threadId),
  );
  if (inRightGroup && !inWrongGroup) return pages;

  const without = pages.map((page) => ({
    pinned: page.pinned.filter((candidate) => candidate.id !== threadId),
    rest: page.rest.filter((candidate) => candidate.id !== threadId),
  }));

  // Bucket 0 is the rows awaiting the user, 1 is the rest. Without `awaitsUser`
  // every row lands in bucket 1 and the date below decides on its own.
  const bucketOf = (candidate: T) => (awaitsUser?.(candidate) ? 0 : 1);
  const rowBucket = bucketOf(row);
  /** Whether the bucket alone seats `row` ahead of `other`, or undefined when they share one. */
  const bucketSeatsFirst = (other: T) => {
    const otherBucket = bucketOf(other);
    return otherBucket === rowBucket ? undefined : otherBucket > rowBucket;
  };

  if (isPinned) {
    const first = without[0];
    // Pins seat newest-first whatever `order` the remainder is read in.
    const index = first.pinned.findIndex(
      (other) => bucketSeatsFirst(other) ?? time(other.pinnedAt) < time(row.pinnedAt),
    );
    const at = index === -1 ? first.pinned.length : index;
    without[0] = { ...first, pinned: insertAt(first.pinned, at, row) };
    return without;
  }

  const sortsAfter = (other: T) =>
    bucketSeatsFirst(other) ??
    (order === "desc"
      ? time(other.sortDate) < time(row.sortDate)
      : time(other.sortDate) > time(row.sortDate));
  for (const [pageIndex, page] of without.entries()) {
    const index = page.rest.findIndex(sortsAfter);
    if (index === -1) continue;
    without[pageIndex] = { ...page, rest: insertAt(page.rest, index, row) };
    return without;
  }
  const last = without.length - 1;
  without[last] = { ...without[last], rest: [...without[last].rest, row] };
  return without;
};

const insertAt = <T>(rows: T[], index: number, row: T): T[] => [
  ...rows.slice(0, index),
  row,
  ...rows.slice(index),
];

/**
 * The key fields whose change means a different list rather than the same list
 * in a different order: the filters, plus `limit`, because cached pages are
 * offset windows of that size and do not line up across sizes.
 *
 * `order` and `needsAttentionFirst` are absent because both reorder the same
 * selection, which is the move whose rows stay worth showing. A field that
 * narrows what the list returns has to be added here, or a change to it will be
 * held across.
 */
const SELECTING_FIELDS = ["status", "activityState", "channel", "labels", "limit"] as const;

/**
 * Whether a cached list query draws from the same set of threads as this one,
 * differing at most in the order they come back in.
 *
 * Both apps hold the previous page set on screen while a list key moves, so the
 * list does not blank when the user flips the order or when a stored preference
 * lands behind the first fetch. That is only safe when both keys select the
 * same threads: across a filter change the previous view's threads would sit
 * under the new one, still interactive, with nothing marking them stale. The
 * held rows can still be the wrong window of the new order, which is the
 * intended trade.
 *
 * Assumes the key both apps build: `["threads", "list", params]`. A factory
 * that reshapes it makes this return false forever, and the only symptom is the
 * list quietly going back to blanking, so each app pins the shape in its own
 * test.
 */
export const sameThreadSelection = (
  cachedKey: readonly unknown[] | undefined,
  // Requires every selecting field by name, so renaming one in a key without
  // renaming it here is a compile error rather than a comparison that
  // quietly stops happening. The index signature lets a key carry its
  // ordering fields too.
  current: Record<(typeof SELECTING_FIELDS)[number], unknown> & Record<string, unknown>,
): boolean => {
  if (cachedKey?.[1] !== "list") return false;
  const cached = cachedKey[2];
  if (typeof cached !== "object" || cached === null) return false;
  const cachedFields: Map<string, unknown> = new Map(Object.entries(cached));
  return SELECTING_FIELDS.every(
    (field) => JSON.stringify(cachedFields.get(field)) === JSON.stringify(current[field]),
  );
};
